import { useMemo, useState } from "react";
import { Button, Chip, Modal, Select, type Column } from "@legal-500/legal500-design-system";
import {
  aiParser,
  DATA_CLASSIFICATION_LABELS,
  type AiMarkdownTemplate,
} from "@/api/aiParser.service";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { FILENAME_PLACEHOLDERS, TemplateFormModal } from "./TemplateFormModal";
import styles from "./AiMarkdownTemplates.module.css";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function AiMarkdownTemplatesPage() {
  const { hasPermission } = usePermissions();
  const canConfigure = hasPermission("submission.config");

  const [schemaFilter, setSchemaFilter] = useState("");

  const { data: schemasData } = useQuery(() => aiParser.listSchemas(), []);
  const schemas = useMemo(() => schemasData?.schemas ?? [], [schemasData]);

  const { data, loading, error, refetch } = useQuery(
    () => aiParser.listMarkdownTemplates(schemaFilter ? { aiSchemaId: schemaFilter } : undefined),
    [schemaFilter],
  );
  const templates = useMemo(() => data?.markdownTemplates ?? [], [data]);

  const schemaNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const schema of schemas) map.set(schema.id, schema.name);
    return map;
  }, [schemas]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AiMarkdownTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<AiMarkdownTemplate | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState(false);

  async function handleSubmit(input: Parameters<typeof aiParser.createMarkdownTemplate>[0]) {
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        await aiParser.updateMarkdownTemplate(editing.id, input);
      } else {
        await aiParser.createMarkdownTemplate(input);
      }
      setFormOpen(false);
      setEditing(null);
      await refetch();
    } catch (caught) {
      setSaveError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeletePending(true);
    setDeleteError(null);
    try {
      await aiParser.deleteMarkdownTemplate(deleting.id);
      setDeleting(null);
      await refetch();
    } catch (caught) {
      setDeleteError(errorMessage(caught));
    } finally {
      setDeletePending(false);
    }
  }

  const columns: Column<AiMarkdownTemplate>[] = useMemo(
    () => [
      {
        header: "Name",
        accessor: "name",
        render: (_value, row) => <span className={styles.nameText}>{row.name}</span>,
      },
      {
        header: "Schema",
        accessor: "aiSchemaId",
        render: (_value, row) => schemaNameById.get(row.aiSchemaId) ?? row.aiSchemaId,
      },
      {
        header: "Filename format",
        accessor: "filenameFormat",
        render: (_value, row) => <code className={styles.mono}>{row.filenameFormat}</code>,
      },
      {
        header: "Classification",
        accessor: "dataClassification",
        render: (_value, row) => (
          <span className={styles.classification} data-level={row.dataClassification}>
            {DATA_CLASSIFICATION_LABELS[row.dataClassification] ?? row.dataClassification}
          </span>
        ),
      },
      {
        header: "Enabled",
        accessor: "enabled",
        render: (_value, row) => (
          <Chip
            size="small"
            variant="filled"
            className={row.enabled ? styles.onChip : styles.offChip}
          >
            {row.enabled ? "Enabled" : "Disabled"}
          </Chip>
        ),
      },
      {
        header: "Actions",
        accessor: "id",
        render: (_value, row) => (
          <div className={styles.rowActions}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditing(row);
                setSaveError(null);
                setFormOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteError(null);
                setDeleting(row);
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [schemaNameById],
  );

  if (!canConfigure) {
    return (
      <UnauthorizedView message="You need the submission.config permission to configure markdown templates." />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="AI Markdown Templates"
          subtitle="EJS renderings of an AI schema's output, written alongside its JSON"
        >
          <Button
            variant="contained"
            onClick={() => {
              setEditing(null);
              setSaveError(null);
              setFormOpen(true);
            }}
          >
            New template
          </Button>
        </PageHeader>
      </div>

      <div className={styles.filterSection}>
        <Select
          label="Schema"
          value={schemaFilter}
          onChange={event => setSchemaFilter(event.target.value)}
          options={[
            { value: "", label: "All schemas" },
            ...schemas.map(schema => ({ value: schema.id, label: schema.name })),
          ]}
          size="small"
        />
      </div>

      <p className={styles.placeholderHelp}>
        Filename placeholders:{" "}
        {FILENAME_PLACEHOLDERS.map(placeholder => (
          <code key={placeholder} className={styles.placeholderCode}>
            {placeholder}
          </code>
        ))}
      </p>

      {loading && <LoadingState message="Loading markdown templates..." />}

      {!loading && error && (
        <div className={styles.errorBanner} role="alert">
          Could not load markdown templates: {error.message}
        </div>
      )}

      {!loading && !error && templates.length === 0 && (
        <EmptyState
          variant="empty"
          title="No markdown templates"
          message="A markdown template belongs to one schema and renders its JSON as a .md file."
        />
      )}

      {!loading && !error && templates.length > 0 && (
        <DataTable data={templates} columns={columns} rowKey="id" size="small" />
      )}

      <TemplateFormModal
        isOpen={formOpen}
        template={editing}
        schemas={schemas}
        saving={saving}
        saveError={saveError}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete markdown template"
        size="small"
      >
        <p className={styles.confirmText}>
          Delete <strong>{deleting?.name}</strong>? Files it already wrote to the blob container are
          not removed.
        </p>
        {deleteError && (
          <div className={styles.errorBanner} role="alert">
            {deleteError}
          </div>
        )}
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={() => setDeleting(null)} disabled={deletePending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deletePending}>
            {deletePending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AiMarkdownTemplatesPage;
